import { ActorSubclass, HttpAgent } from '@icp-sdk/core/agent'
import { Principal } from '@icp-sdk/core/principal'
import { hexToBytes } from 'viem'
import { config } from '../appConfig'
import {
  idlFactory as ethHelperFactory,
  _SERVICE as HelperActor,
  utxo,
} from '../idls/ethHelper/ethHelper.did'
import { Chain, EVMChain } from '../interfaces/common'
import {
  ApproveErc20Params,
  BTCSendOrdinalParams,
  BTCSendParams,
  CustomApproveErc20Params,
  EthCallContractParams,
  MintCkEthParams,
  SendErc20Params,
  SendEthParams,
  SolSendParams,
  SplSendParams,
  UTXOSendParams,
} from '../interfaces/walletActions'
import { logger } from '../logger'
import { createActor } from '../utils/createActor'
import { getEthProvider } from '../utils/getEthProvider'
import { fromHex } from '../utils/hex'
import { solanaClient } from './SolanaClient'
import TatumClient from './TatumClient'

const HELPER_CANISTER_ID = 'ajx4k-liaaa-aaaal-ajqfq-cai'

class ChainFusionClient {
  private helperActor: ActorSubclass<HelperActor>

  constructor(agent: HttpAgent) {
    this.helperActor = createActor(ethHelperFactory, {
      canisterId: HELPER_CANISTER_ID,
      agent,
      pollingOptions: { preSignReadStateRequest: false },
    })
  }

  async sendEth(params: SendEthParams) {
    const { address, amount, gasLimit, maxFeePerGas, maxPriorityFeePerGas, nonce, chain } = params

    const signedTransaction = await this.helperActor.send_eth(
      { Sign: null },
      address,
      BigInt(amount),
      BigInt(gasLimit || ''),
      BigInt(maxFeePerGas || ''),
      BigInt(maxPriorityFeePerGas || ''),
      BigInt(nonce),
      BigInt(chain.chainId)
    )

    if ('Err' in signedTransaction) {
      return Promise.reject(signedTransaction.Err)
    }

    if ('Ok' in signedTransaction) {
      return this.sendEthTransaction(signedTransaction.Ok, chain)
    }
  }

  private async publishTransaction(transaction: string, chain: EVMChain) {
    try {
      return await this.sendEthTransaction(transaction, chain)
    } catch (e) {
      logger.debug('ChainFushion publishTransaction error', {
        originalError: e,
      })

      return Promise.reject((e as Error).message)
    }
  }

  private async sendEthTransaction(transaction: string, chain: EVMChain) {
    const provider = getEthProvider(chain.rpcEndpoint)

    const hash = await provider.sendRawTransaction({
      serializedTransaction: transaction as `0x${string}`,
    })

    await provider.waitForTransactionReceipt({ hash, confirmations: 1, pollingInterval: 1_000 })

    return hash
  }

  async sendErc20(params: SendErc20Params) {
    const {
      amount,
      gasLimit,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      contractAddress,
      receiver,
      chain,
    } = params

    const signedTransaction = await this.helperActor.send_erc20(
      { Sign: null },
      BigInt(amount),
      BigInt(gasLimit),
      BigInt(maxFeePerGas),
      BigInt(maxPriorityFeePerGas),
      BigInt(nonce),
      BigInt(chain.chainId),
      contractAddress,
      receiver
    )

    if ('Err' in signedTransaction) {
      return Promise.reject(signedTransaction.Err)
    }

    if ('Ok' in signedTransaction) {
      return this.publishTransaction(signedTransaction.Ok, chain)
    }
  }

  async approveErc20(params: ApproveErc20Params) {
    const { amount, gasLimit, maxFeePerGas, maxPriorityFeePerGas, nonce, contractAddress, chain } =
      params

    const signedTransaction = await this.helperActor.approve_erc20(
      { Sign: null },
      BigInt(amount),
      BigInt(gasLimit),
      BigInt(maxFeePerGas),
      BigInt(maxPriorityFeePerGas),
      BigInt(nonce),
      BigInt(chain.chainId),
      contractAddress
    )

    if ('Err' in signedTransaction) {
      return Promise.reject(signedTransaction.Err)
    }

    if ('Ok' in signedTransaction) {
      return this.publishTransaction(signedTransaction.Ok, chain)
    }
  }

  async depositErc20(params: ApproveErc20Params) {
    const { amount, gasLimit, maxFeePerGas, maxPriorityFeePerGas, nonce, contractAddress, chain } =
      params

    const signedTransaction = await this.helperActor.deposit_erc20(
      { Sign: null },
      BigInt(amount),
      BigInt(gasLimit),
      BigInt(maxFeePerGas),
      BigInt(maxPriorityFeePerGas),
      BigInt(nonce),
      BigInt(chain.chainId),
      contractAddress
    )

    if ('Err' in signedTransaction) {
      return Promise.reject(signedTransaction.Err)
    }

    if ('Ok' in signedTransaction) {
      return this.publishTransaction(signedTransaction.Ok, chain)
    }
  }

  async mintCkEth(params: MintCkEthParams) {
    const response = await this.helperActor.mint_cketh(
      { Sign: null },
      BigInt(params.amount),
      BigInt(params.gasLimit || ''),
      BigInt(params.maxFeePerGas || ''),
      BigInt(params.maxPriorityFeePerGas || ''),
      BigInt(params.nonce),
      BigInt(params.chain.chainId)
    )

    if ('Ok' in response) {
      return this.publishTransaction(response.Ok, params.chain)
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async signEthHash(hash: `0x${string}`) {
    const response = await this.helperActor.sign_hash(hexToBytes(hash))

    if ('Ok' in response) {
      return response.Ok as `0x${string}`
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async ethCallContract(params: EthCallContractParams) {
    const response = await this.helperActor.eth_call_contract(
      { Sign: null },
      BigInt(params.value),
      BigInt(params.gasLimit),
      BigInt(params.maxFeePerGas),
      BigInt(params.maxPriorityFeePerGas),
      BigInt(params.nonce),
      BigInt(params.chain.chainId),
      params.contractAddress,
      params.data
    )

    if ('Ok' in response) {
      return this.publishTransaction(response.Ok, params.chain)
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async customApproveErc20(params: CustomApproveErc20Params) {
    const response = await this.helperActor.custom_approve_erc20(
      { Sign: null },
      BigInt(params.value),
      BigInt(params.gasLimit),
      BigInt(params.maxFeePerGas),
      BigInt(params.maxPriorityFeePerGas),
      BigInt(params.nonce),
      BigInt(params.chain.chainId),
      params.contractAddress,
      params.spender
    )

    if ('Ok' in response) {
      return this.publishTransaction(response.Ok, params.chain)
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async getBtcAddress(principal: Principal) {
    const response = await this.helperActor.btc_p2tr_raw_key_address(
      principal,
      config.fetchEnv('BITCOIN_CHAIN') === 'testnet'
        ? { Testnet: null }
        : {
            Mainnet: null,
          }
    )

    if ('Ok' in response) {
      return response.Ok
    } else {
      throw response.Err
    }
  }

  async sendBTC({ amount, to, fee, utxos }: BTCSendParams) {
    const mappedUtxo: utxo[] = utxos.map((u) => ({
      height: u.mintHeight,
      value: BigInt(u.value),
      outpoint: {
        vout: u.mintIndex,
        txid: Array.from(new Uint8Array(fromHex(u.mintTxid))).reverse(),
      },
    }))

    const response = await this.helperActor.send_btc(
      { Sign: null },
      to,
      BigInt(amount),
      BigInt(fee),
      config.fetchEnv('BITCOIN_CHAIN') === 'testnet' ? { Testnet: null } : { Mainnet: null },
      mappedUtxo
    )

    if ('Ok' in response) {
      const { txid } = await TatumClient.publishTransaction(response.Ok, Chain.BTC)
      return txid
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async sendOrdinal({ to, fee, ordinalUtxo, feeUtxo }: BTCSendOrdinalParams) {
    const mappedOrdinalUtxo: utxo = {
      height: ordinalUtxo.mintHeight,
      value: BigInt(ordinalUtxo.value),
      outpoint: {
        vout: ordinalUtxo.mintIndex,
        txid: Array.from(new Uint8Array(fromHex(ordinalUtxo.mintTxid))).reverse(),
      },
    }

    const mappedFeeUtxo: utxo = {
      height: feeUtxo.mintHeight,
      value: BigInt(feeUtxo.value),
      outpoint: {
        vout: feeUtxo.mintIndex,
        txid: Array.from(new Uint8Array(fromHex(feeUtxo.mintTxid))).reverse(),
      },
    }

    const response = await this.helperActor.send_ordinal(
      { Sign: null },
      to,
      BigInt(fee),
      config.fetchEnv('BITCOIN_CHAIN') === 'testnet' ? { Testnet: null } : { Mainnet: null },
      mappedOrdinalUtxo,
      mappedFeeUtxo
    )

    if ('Ok' in response) {
      const { txid } = await TatumClient.publishTransaction(response.Ok, Chain.BTC)
      return txid
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async sendUTXO({ chain, amount, to, fee, utxos }: UTXOSendParams) {
    const mappedUtxo: utxo[] = utxos.map((u) => ({
      height: u.mintHeight,
      value: BigInt(u.value),
      outpoint: {
        vout: u.mintIndex,
        txid: Array.from(new Uint8Array(fromHex(u.mintTxid))).reverse(),
      },
    }))

    const isTestnet = config.fetchEnv('BITCOIN_CHAIN') === 'testnet'

    let response: { Ok: string } | { Err: string } | undefined

    if (chain === Chain.DOGE || chain === Chain.LTC) {
      response = await this.helperActor.sign_doge(
        to,
        BigInt(amount),
        BigInt(fee),
        mappedUtxo,
        !isTestnet
      )
    } else {
      const networkVariant = isTestnet ? { Testnet: null } : { Mainnet: null }
      response = await this.helperActor.send_btc(
        { Sign: null },
        to,
        BigInt(amount),
        BigInt(fee),
        networkVariant,
        mappedUtxo
      )
    }

    if (response && 'Ok' in response) {
      const { txid } = await TatumClient.publishTransaction(response.Ok, chain)
      return txid
    }

    if (response && 'Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  getEthAddress(principal: Principal) {
    return this.helperActor.eth_address([principal])
  }

  async getSolAddress(principal: Principal) {
    const response = await this.helperActor.solana_address(principal)

    if ('Ok' in response) {
      return response.Ok
    } else {
      throw response.Err
    }
  }

  getDogeAddress(principal: Principal) {
    return this.helperActor.doge_address(
      [principal],
      config.fetchEnv('BITCOIN_CHAIN') === 'mainnet'
    )
  }

  sendSol(solSendParams: SolSendParams) {
    return solanaClient.sendSol(solSendParams, this.helperActor)
  }

  sendSpl(splSendParams: SplSendParams) {
    return solanaClient.sendSpl(splSendParams, this.helperActor)
  }

  async signSolMessage(message: Uint8Array | number[]) {
    const response = await this.helperActor.sign_sol(message)

    if ('Ok' in response) {
      return [...response.Ok]
    }

    if ('Err' in response) {
      throw response.Err
    }
  }
}

export default ChainFusionClient
